import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import Sidebar from '../admin-dashboard/components/Sidebar';
import AdminBottomNav from '../admin-dashboard/components/AdminBottomNav';
import AdminMobileHeader from '../admin-dashboard/components/AdminMobileHeader';

// ─── Helpers ────────────────────────────────────────────────────────────────

const getCurrentPeriod = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const getPrevPeriod = (period) => {
  const [y, m] = period.split('-').map(Number);
  const d = new Date(y, m - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const formatCurrency = (val) => {
  if (val === null || val === undefined || isNaN(val)) return '$0.00';
  const neg = val < 0;
  return (neg ? '-$' : '$') + Math.abs(Number(val)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatPct = (val) => {
  if (val === null || val === undefined || isNaN(val) || !isFinite(val)) return '-';
  const sign = val > 0 ? '+' : '';
  return `${sign}${val.toFixed(1)}%`;
};

const calcCommission = (bonus, charge, inc, rate) => {
  const b = bonus ?? 0, c = charge ?? 0, i = inc ?? 0, r = rate ?? 50;
  return ((b - i - c) / 2) + (i * r / 100);
};

// ─── Main Component ──────────────────────────────────────────────────────────

const ManagerRates = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [adminId, setAdminId] = useState(null);

  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(getCurrentPeriod());
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Data: { [managerId]: { servers: { FR: { bonus, charge, inc, diamonds, creatorsCount }, ... } } }
  const [managerData, setManagerData] = useState({});
  const [prevData, setPrevData] = useState({});

  // Rates: { "managerId__server": { rate, notes, saving, saved } }
  const [rateState, setRateState] = useState({});

  const [bulkRate, setBulkRate] = useState(50);
  const [bulkSaving, setBulkSaving] = useState(false);

  const [expandedManager, setExpandedManager] = useState(null);

  // Sort
  const [sortCol, setSortCol] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  // Commission generation
  const [showGenConfirm, setShowGenConfirm] = useState(false);
  const [genLoading, setGenLoading] = useState(false);
  const [genResult, setGenResult] = useState(null);

  // Auth
  useEffect(() => {
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      if (session?.user) { setUserEmail(session.user.email || ''); setAdminId(session.user.id); }
    });
  }, []);

  // Fetch periods
  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase?.from('creator_monthly_history')?.select('period')?.not('period', 'is', null);
      const unique = [...new Set((data || []).map(p => p.period))].sort((a, b) => b.localeCompare(a));
      setPeriods(unique);
      if (unique.length > 0 && !unique.includes(selectedPeriod)) setSelectedPeriod(unique[0]);
    };
    fetch();
  }, []);

  // Fetch managers
  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase?.from('users')?.select('id, name, email')?.in('role', ['manager', 'region_manager'])?.order('name');
      setManagers(data || []);
    };
    fetch();
  }, []);

  // Load data for period
  const loadPeriodData = useCallback(async () => {
    if (!managers.length) return;
    setLoading(true);
    const prevPeriod = getPrevPeriod(selectedPeriod);

    const [curRes, prevRes, ratesRes] = await Promise.all([
      supabase?.from('creator_monthly_history')?.select('manager_id, server, total_bonus, charge, incremental_bonus, diamonds, tiktok_id')?.eq('period', selectedPeriod)?.not('manager_id', 'is', null),
      supabase?.from('creator_monthly_history')?.select('manager_id, server, diamonds, tiktok_id')?.eq('period', prevPeriod)?.not('manager_id', 'is', null),
      supabase?.from('manager_rates')?.select('manager_id, server, incremental_rate, notes')?.eq('period', selectedPeriod),
    ]);

    // Aggregate current
    const mData = {};
    (curRes.data || []).forEach(r => {
      const mid = r.manager_id, srv = r.server;
      if (!mData[mid]) mData[mid] = { servers: {} };
      if (!mData[mid].servers[srv]) mData[mid].servers[srv] = { bonus: 0, charge: 0, inc: 0, diamonds: 0, creators: new Set() };
      const s = mData[mid].servers[srv];
      s.bonus += Number(r.total_bonus) || 0;
      s.charge += Number(r.charge) || 0;
      s.inc += Number(r.incremental_bonus) || 0;
      s.diamonds += Number(r.diamonds) || 0;
      s.creators.add(r.tiktok_id);
    });
    Object.values(mData).forEach(m => Object.values(m.servers).forEach(s => { s.creatorsCount = s.creators.size; delete s.creators; }));

    // Aggregate prev
    const pData = {};
    (prevRes.data || []).forEach(r => {
      const mid = r.manager_id, srv = r.server;
      if (!pData[mid]) pData[mid] = { servers: {} };
      if (!pData[mid].servers[srv]) pData[mid].servers[srv] = { diamonds: 0, creators: new Set() };
      pData[mid].servers[srv].diamonds += Number(r.diamonds) || 0;
      pData[mid].servers[srv].creators.add(r.tiktok_id);
    });
    Object.values(pData).forEach(m => Object.values(m.servers).forEach(s => { s.creatorsCount = s.creators.size; delete s.creators; }));

    // Rates map
    const rMap = {};
    (ratesRes.data || []).forEach(r => { rMap[`${r.manager_id}__${r.server}`] = r; });

    // Init rate state
    const rs = {};
    managers.forEach(mgr => {
      const md = mData[mgr.id];
      if (!md) return;
      Object.keys(md.servers).forEach(srv => {
        const key = `${mgr.id}__${srv}`;
        rs[key] = {
          rate: rMap[key]?.incremental_rate ?? 50,
          notes: rMap[key]?.notes || '',
          saving: false,
          saved: !!rMap[key],
        };
      });
    });

    setManagerData(mData);
    setPrevData(pData);
    setRateState(rs);
    setLoading(false);
  }, [selectedPeriod, managers]);

  useEffect(() => { loadPeriodData(); }, [loadPeriodData]);

  // Get manager totals
  const getManagerTotals = (mgrId) => {
    const md = managerData[mgrId];
    const pd = prevData[mgrId];
    if (!md) return { bonus: 0, charge: 0, inc: 0, diamonds: 0, creatorsCount: 0, diamondsM1: 0, creatorsM1: 0, commission: 0 };
    let bonus = 0, charge = 0, inc = 0, diamonds = 0, creatorsCount = 0, commission = 0;
    let diamondsM1 = 0, creatorsM1 = 0;
    Object.entries(md.servers).forEach(([srv, s]) => {
      bonus += s.bonus; charge += s.charge; inc += s.inc; diamonds += s.diamonds; creatorsCount += s.creatorsCount;
      const rate = parseFloat(rateState[`${mgrId}__${srv}`]?.rate) || 50;
      commission += calcCommission(s.bonus, s.charge, s.inc, rate);
    });
    if (pd) Object.values(pd.servers).forEach(s => { diamondsM1 += s.diamonds; creatorsM1 += s.creatorsCount; });
    return { bonus, charge, inc, diamonds, creatorsCount, diamondsM1, creatorsM1, commission };
  };

  // Save rate for manager+server
  const saveRate = async (mgrId, server) => {
    const key = `${mgrId}__${server}`;
    const row = rateState[key];
    if (!row) return;
    setRateState(prev => ({ ...prev, [key]: { ...prev[key], saving: true } }));
    await supabase?.from('manager_rates')?.upsert({
      manager_id: mgrId, period: selectedPeriod, server,
      incremental_rate: parseFloat(row.rate) || 50, notes: row.notes || '', created_by: adminId,
    }, { onConflict: 'manager_id,period,server' });
    setRateState(prev => ({ ...prev, [key]: { ...prev[key], saving: false, saved: true } }));
  };

  // Save all rates for a manager (global rate)
  const saveManagerGlobalRate = async (mgrId, rate) => {
    const md = managerData[mgrId];
    if (!md) return;
    const servers = Object.keys(md.servers);
    const newState = { ...rateState };
    for (const srv of servers) {
      const key = `${mgrId}__${srv}`;
      newState[key] = { ...newState[key], rate, saving: true };
    }
    setRateState(newState);

    const upserts = servers.map(srv => ({
      manager_id: mgrId, period: selectedPeriod, server: srv,
      incremental_rate: parseFloat(rate) || 50, notes: rateState[`${mgrId}__${srv}`]?.notes || '', created_by: adminId,
    }));
    await supabase?.from('manager_rates')?.upsert(upserts, { onConflict: 'manager_id,period,server' });

    const finalState = { ...rateState };
    servers.forEach(srv => { const key = `${mgrId}__${srv}`; finalState[key] = { ...finalState[key], rate, saving: false, saved: true }; });
    setRateState(finalState);
  };

  // Bulk apply
  const handleApplyBulk = async () => {
    setBulkSaving(true);
    const upserts = [];
    const newState = { ...rateState };
    managers.forEach(mgr => {
      const md = managerData[mgr.id];
      if (!md) return;
      Object.keys(md.servers).forEach(srv => {
        const key = `${mgr.id}__${srv}`;
        newState[key] = { ...newState[key], rate: parseFloat(bulkRate) || 50, saved: true };
        upserts.push({
          manager_id: mgr.id, period: selectedPeriod, server: srv,
          incremental_rate: parseFloat(bulkRate) || 50, notes: newState[key]?.notes || '', created_by: adminId,
        });
      });
    });
    await supabase?.from('manager_rates')?.upsert(upserts, { onConflict: 'manager_id,period,server' });
    setRateState(newState);
    setBulkSaving(false);
  };

  // Generate commissions
  const handleGenerateCommissions = async () => {
    setGenLoading(true);
    setGenResult(null);
    try {
      // Save all rates first
      const upserts = [];
      managers.forEach(mgr => {
        const md = managerData[mgr.id];
        if (!md) return;
        Object.keys(md.servers).forEach(srv => {
          const key = `${mgr.id}__${srv}`;
          upserts.push({
            manager_id: mgr.id, period: selectedPeriod, server: srv,
            incremental_rate: parseFloat(rateState[key]?.rate) || 50, notes: rateState[key]?.notes || '', created_by: adminId,
          });
        });
      });
      await supabase?.from('manager_rates')?.upsert(upserts, { onConflict: 'manager_id,period,server' });

      // Delete existing
      await supabase?.from('manager_transactions')?.delete()?.eq('type', 'commission')?.eq('period', selectedPeriod);

      // Insert
      const [y, m] = selectedPeriod.split('-');
      const monthName = t(`months.${parseInt(m, 10) - 1}`);
      const createdAt = new Date(`${selectedPeriod}-01`);
      createdAt.setMonth(createdAt.getMonth() + 1); createdAt.setDate(8);

      const toInsert = [];
      managers.forEach(mgr => {
        const md = managerData[mgr.id];
        if (!md) return;
        Object.entries(md.servers).forEach(([server, srvData]) => {
          const rate = parseFloat(rateState[`${mgr.id}__${server}`]?.rate) || 50;
          const commission = calcCommission(srvData.bonus, srvData.charge, srvData.inc, rate);
          if (Math.round(commission * 100) === 0) return;
          toInsert.push({
            manager_id: mgr.id, type: 'commission',
            amount: Math.round(commission * 100) / 100,
            description: t('payments.commissionFor', { month: monthName, year: y, server }),
            period: selectedPeriod, server, created_at: createdAt.toISOString(),
          });
        });
      });

      if (toInsert.length > 0) {
        const { error } = await supabase?.from('manager_transactions')?.insert(toInsert);
        if (error) throw error;
      }
      setGenResult({ success: true, message: t('payments.commissionsGenerated', { count: toInsert.length, month: monthName, year: y }) });
    } catch (err) {
      setGenResult({ success: false, message: err?.message || 'Erreur.' });
    }
    setGenLoading(false);
    setShowGenConfirm(false);
  };

  // KPIs
  const totals = useMemo(() => {
    let totalCommission = 0, totalBonus = 0, totalCharge = 0;
    managers.forEach(m => { const t = getManagerTotals(m.id); totalCommission += t.commission; totalBonus += t.bonus; totalCharge += t.charge; });
    return { totalCommission, totalBonus, totalCharge, netAgency: totalBonus - totalCharge - totalCommission };
  }, [managers, managerData, rateState]);

  // Sort
  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const sortedManagers = useMemo(() => {
    return [...managers].sort((a, b) => {
      const ta = getManagerTotals(a.id);
      const tb = getManagerTotals(b.id);
      let va, vb;
      switch (sortCol) {
        case 'name': va = a.name?.toLowerCase(); vb = b.name?.toLowerCase(); break;
        case 'creators': va = ta.creatorsCount; vb = tb.creatorsCount; break;
        case 'diamonds': va = ta.diamonds; vb = tb.diamonds; break;
        case 'evol': va = ta.diamondsM1 > 0 ? (ta.diamonds - ta.diamondsM1) / ta.diamondsM1 : -999; vb = tb.diamondsM1 > 0 ? (tb.diamonds - tb.diamondsM1) / tb.diamondsM1 : -999; break;
        case 'bonus': va = ta.bonus; vb = tb.bonus; break;
        case 'charge': va = ta.charge; vb = tb.charge; break;
        case 'commission': va = ta.commission; vb = tb.commission; break;
        default: va = a.name?.toLowerCase(); vb = b.name?.toLowerCase();
      }
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? (va - vb) : (vb - va);
    });
  }, [managers, managerData, rateState, sortCol, sortDir]);

  const SortHeader = ({ label, col, align }) => (
    <th
      className={`px-3 sm:px-4 py-3 text-xs font-medium cursor-pointer select-none transition-colors ${align === 'left' ? 'text-left' : 'text-right'}`}
      style={{ color: sortCol === col ? '#E5202A' : 'rgba(255,255,255,0.35)' }}
      onClick={() => handleSort(col)}
    >
      {label} {sortCol === col && (sortDir === 'asc' ? '↑' : '↓')}
    </th>
  );

  return (
    <div className="flex h-[100dvh] overflow-hidden" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="hidden md:flex">
        <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(c => !c)} userEmail={userEmail} />
      </div>
      <main className="flex-1 overflow-y-auto pb-16 md:pb-0" style={{ backgroundColor: 'var(--color-background)' }}>
        <AdminMobileHeader title={t('admin.rates.title', 'Taux & Commissions')} />
        <div className="p-4 sm:p-6 max-w-7xl mx-auto">

          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{t('admin.rates.title', 'Taux & Commissions')}</h1>
              <p className="text-sm mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>{t('admin.rates.subtitle', 'Fixez les taux par manager/serveur et générez les commissions.')}</p>
            </div>
            <div className="flex items-center gap-3">
              <select value={selectedPeriod} onChange={e => { setSelectedPeriod(e.target.value); setGenResult(null); setExpandedManager(null); }}
                className="rounded-lg px-3 py-2 text-base font-medium text-white border outline-none"
                style={{ backgroundColor: '#1a1a1a', borderColor: '#2a2a2a', minWidth: '130px' }}>
                {periods.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <button onClick={() => setShowGenConfirm(true)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
                style={{ backgroundColor: 'var(--color-primary)' }}
                onMouseEnter={e => e.currentTarget.style.backgroundColor = '#cc0000'}
                onMouseLeave={e => e.currentTarget.style.backgroundColor = '#E5202A'}>
                {t('admin.rates.generateCommissions', 'Générer commissions')}
              </button>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {[
              { label: t('admin.rates.totalBonus', 'Total Bonus'), value: totals.totalBonus, color: '#22c55e' },
              { label: t('admin.rates.totalCharge', 'Total Charge'), value: totals.totalCharge, color: '#f59e0b' },
              { label: t('admin.rates.commissionsManagers', 'Commissions Managers'), value: totals.totalCommission, color: '#a855f7' },
              { label: t('admin.rates.netAgency', 'Net Agence'), value: totals.netAgency, color: totals.netAgency >= 0 ? '#22c55e' : '#ef4444' },
            ].map((kpi, i) => (
              <div key={i} className="rounded-xl p-4 border" style={{ backgroundColor: '#0d0d0d', borderColor: '#1f1f1f' }}>
                <span className="text-xs font-medium block mb-2" style={{ color: 'rgba(255,255,255,0.45)' }}>{kpi.label}</span>
                <p className="text-lg sm:text-xl font-bold" style={{ color: kpi.color, fontFamily: 'Sora, sans-serif' }}>{formatCurrency(kpi.value)}</p>
              </div>
            ))}
          </div>

          {/* Bulk */}
          <div className="flex flex-wrap items-center gap-3 p-4 rounded-xl mb-6" style={{ backgroundColor: '#0d0d0d', border: '1px solid #1f1f1f' }}>
            <span className="text-sm font-medium text-white">{t('admin.rates.bulkRate', 'Taux groupé :')}</span>
            <input type="number" min={0} max={100} step={0.5} value={bulkRate} onChange={e => setBulkRate(e.target.value)}
              className="w-20 rounded-lg px-3 py-1.5 text-base text-white border outline-none text-center"
              style={{ backgroundColor: '#1a1a1a', borderColor: '#2a2a2a' }} />
            <span className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>%</span>
            <button onClick={handleApplyBulk} disabled={bulkSaving}
              className="px-4 py-1.5 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
              style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid #2a2a2a' }}>
              {bulkSaving ? t('admin.rates.applying', 'Application...') : t('admin.rates.applyAll', 'Appliquer à tous')}
            </button>
          </div>

          {/* Table */}
          <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#0d0d0d', borderColor: '#1f1f1f' }}>
            {loading ? (
              <div className="p-8 text-center" style={{ color: 'rgba(255,255,255,0.3)' }}>{t('admin.rates.loading', 'Chargement...')}</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr style={{ borderBottom: '1px solid #1f1f1f' }}>
                      <SortHeader label="Manager" col="name" align="left" />
                      <SortHeader label={t('admin.rates.colCreators', 'Creators')} col="creators" />
                      <SortHeader label="Diamonds" col="diamonds" />
                      <SortHeader label="Evol." col="evol" />
                      <SortHeader label="Total Bonus" col="bonus" />
                      <SortHeader label="Charge" col="charge" />
                      <th className="px-3 sm:px-4 py-3 text-xs font-medium text-right" style={{ color: 'rgba(255,255,255,0.35)' }}>{t('admin.rates.colIncremental', 'Incremental')}</th>
                      <th className="px-3 sm:px-4 py-3 text-xs font-medium text-right" style={{ color: 'rgba(255,255,255,0.35)' }}>{t('admin.rates.colGlobalRate', 'Global rate')}</th>
                      <SortHeader label="Commission" col="commission" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedManagers.map(mgr => {
                      const t = getManagerTotals(mgr.id);
                      const md = managerData[mgr.id];
                      const pd = prevData[mgr.id];
                      const newCreators = t.creatorsCount - t.creatorsM1;
                      const evolPct = t.diamondsM1 > 0 ? ((t.diamonds - t.diamondsM1) / t.diamondsM1) * 100 : null;
                      const isExpanded = expandedManager === mgr.id;

                      // Check if all servers have same rate
                      const serverRates = md ? Object.keys(md.servers).map(srv => parseFloat(rateState[`${mgr.id}__${srv}`]?.rate) || 50) : [];
                      const allSameRate = serverRates.length > 0 && serverRates.every(r => r === serverRates[0]);
                      const globalRate = allSameRate ? serverRates[0] : '';

                      return (
                        <React.Fragment key={mgr.id}>
                          <tr className="cursor-pointer transition-colors"
                            style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', backgroundColor: isExpanded ? 'rgba(229,32,42,0.04)' : 'transparent' }}
                            onClick={() => setExpandedManager(isExpanded ? null : mgr.id)}
                            onMouseEnter={e => { if (!isExpanded) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.02)'; }}
                            onMouseLeave={e => { if (!isExpanded) e.currentTarget.style.backgroundColor = 'transparent'; }}>
                            <td className="px-3 sm:px-4 py-3 text-sm font-medium text-white">
                              <div className="flex items-center gap-2">
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ transform: isExpanded ? 'rotate(90deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
                                  <path d="M4 2l4 4-4 4" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                                {mgr.name}
                                {newCreators > 0 && <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(34,197,94,0.15)', color: '#22c55e' }}>+{newCreators}</span>}
                              </div>
                            </td>
                            <td className="px-3 sm:px-4 py-3 text-sm text-right text-white/60">
                              {t.creatorsCount}{t.creatorsM1 > 0 && <span style={{ color: 'rgba(255,255,255,0.25)' }}> / {t.creatorsM1}</span>}
                            </td>
                            <td className="px-3 sm:px-4 py-3 text-sm text-right text-white/60">{t.diamonds > 0 ? t.diamonds.toLocaleString() : '-'}</td>
                            <td className="px-3 sm:px-4 py-3 text-sm text-right font-medium" style={{ color: evolPct === null ? 'rgba(255,255,255,0.25)' : evolPct >= 0 ? '#22c55e' : '#ef4444' }}>
                              {formatPct(evolPct)}
                            </td>
                            <td className="px-3 sm:px-4 py-3 text-sm text-right" style={{ color: '#22c55e' }}>{formatCurrency(t.bonus)}</td>
                            <td className="px-3 sm:px-4 py-3 text-sm text-right" style={{ color: '#f59e0b' }}>{formatCurrency(t.charge)}</td>
                            <td className="px-3 sm:px-4 py-3 text-sm text-right text-white/60">{formatCurrency(t.inc)}</td>
                            <td className="px-3 sm:px-4 py-3 text-right" onClick={e => e.stopPropagation()}>
                              <input type="number" min={0} max={100} step={0.5}
                                value={globalRate}
                                placeholder="..."
                                onChange={e => {
                                  const val = e.target.value;
                                  if (val === '') return;
                                  saveManagerGlobalRate(mgr.id, val);
                                }}
                                className="w-16 rounded px-2 py-1 text-base text-white text-center border outline-none"
                                style={{ backgroundColor: '#1a1a1a', borderColor: allSameRate ? '#2a2a2a' : '#f59e0b' }}
                                title={allSameRate ? `${globalRate}% sur tous les serveurs` : 'Taux différents par serveur — ouvrir pour détail'}
                              />
                            </td>
                            <td className="px-3 sm:px-4 py-3 text-sm text-right font-bold" style={{ color: t.commission >= 0 ? '#a855f7' : '#ef4444' }}>
                              {formatCurrency(t.commission)}
                            </td>
                          </tr>

                          {/* Expanded: server detail */}
                          {isExpanded && md && (
                            Object.entries(md.servers).sort(([a], [b]) => a.localeCompare(b)).map(([server, srvData]) => {
                              const key = `${mgr.id}__${server}`;
                              const srvPrev = pd?.servers?.[server];
                              const srvRate = parseFloat(rateState[key]?.rate) || 50;
                              const srvCommission = calcCommission(srvData.bonus, srvData.charge, srvData.inc, srvRate);
                              const srvEvolPct = srvPrev?.diamonds > 0 ? ((srvData.diamonds - srvPrev.diamonds) / srvPrev.diamonds) * 100 : null;
                              const srvNew = srvData.creatorsCount - (srvPrev?.creatorsCount || 0);
                              const row = rateState[key];

                              return (
                                <tr key={key} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)', backgroundColor: 'rgba(229,32,42,0.02)' }}>
                                  <td className="px-3 sm:px-4 py-2 text-xs pl-10">
                                    <span className="px-2 py-0.5 rounded text-white/60" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>{server}</span>
                                  </td>
                                  <td className="px-3 sm:px-4 py-2 text-xs text-right text-white/40">
                                    {srvData.creatorsCount}{srvPrev ? ` / ${srvPrev.creatorsCount}` : ''}
                                    {srvNew > 0 && <span style={{ color: '#22c55e' }}> +{srvNew}</span>}
                                  </td>
                                  <td className="px-3 sm:px-4 py-2 text-xs text-right text-white/40">{srvData.diamonds.toLocaleString()}</td>
                                  <td className="px-3 sm:px-4 py-2 text-xs text-right" style={{ color: srvEvolPct === null ? 'rgba(255,255,255,0.2)' : srvEvolPct >= 0 ? '#22c55e' : '#ef4444' }}>
                                    {formatPct(srvEvolPct)}
                                  </td>
                                  <td className="px-3 sm:px-4 py-2 text-xs text-right" style={{ color: 'rgba(34,197,94,0.7)' }}>{formatCurrency(srvData.bonus)}</td>
                                  <td className="px-3 sm:px-4 py-2 text-xs text-right" style={{ color: 'rgba(245,158,11,0.7)' }}>{formatCurrency(srvData.charge)}</td>
                                  <td className="px-3 sm:px-4 py-2 text-xs text-right text-white/30">{formatCurrency(srvData.inc)}</td>
                                  <td className="px-3 sm:px-4 py-2 text-right" onClick={e => e.stopPropagation()}>
                                    <div className="flex items-center justify-end gap-1">
                                      <input type="number" min={0} max={100} step={0.5}
                                        value={row?.rate ?? 50}
                                        onChange={e => setRateState(prev => ({ ...prev, [key]: { ...prev[key], rate: e.target.value, saved: false } }))}
                                        className="w-14 rounded px-2 py-1 text-base text-white text-center border outline-none"
                                        style={{ backgroundColor: '#1a1a1a', borderColor: row?.saved ? '#22c55e' : '#2a2a2a' }}
                                      />
                                      <button onClick={() => saveRate(mgr.id, server)} disabled={row?.saving}
                                        className="px-1.5 py-1 rounded text-xs transition-colors"
                                        style={{ color: row?.saved ? '#22c55e' : 'rgba(255,255,255,0.4)', backgroundColor: row?.saved ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.04)' }}>
                                        {row?.saving ? '..' : row?.saved ? 'OK' : 'Save'}
                                      </button>
                                    </div>
                                  </td>
                                  <td className="px-3 sm:px-4 py-2 text-xs text-right font-semibold" style={{ color: srvCommission >= 0 ? 'rgba(168,85,247,0.7)' : 'rgba(239,68,68,0.7)' }}>
                                    {formatCurrency(srvCommission)}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      <AdminBottomNav />

      {/* Modal confirm */}
      {showGenConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
          <div className="w-full max-w-md rounded-xl p-6 border" style={{ backgroundColor: '#111', borderColor: '#1f1f1f' }}>
            <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>{t('admin.rates.generateModal.title', 'Générer les commissions')}</h3>
            <p className="text-sm mb-4" style={{ color: 'rgba(255,255,255,0.5)' }}>
              Cette action va mettre à jour les commissions des managers pour <strong className="text-white">{selectedPeriod}</strong>. Les commissions existantes seront remplacées.
            </p>
            {genResult && (
              <div className="mb-4 p-3 rounded-lg text-sm" style={{
                backgroundColor: genResult.success ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)',
                color: genResult.success ? '#22c55e' : '#ef4444',
              }}>{genResult.message}</div>
            )}
            <div className="flex gap-3 justify-end">
              <button onClick={() => { setShowGenConfirm(false); setGenResult(null); }}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white/60 hover:text-white" style={{ border: '1px solid #1f1f1f' }}>
                {genResult?.success ? 'Fermer' : 'Annuler'}
              </button>
              {!genResult?.success && (
                <button onClick={handleGenerateCommissions} disabled={genLoading}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50" style={{ backgroundColor: 'var(--color-primary)' }}>
                  {genLoading ? 'Calcul en cours...' : 'Confirmer'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManagerRates;
